import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { CTAButton } from "@/components/common/CTA";
import { RouteLines } from "@/components/common/RouteLines";
import { useSettings } from "@/context/SettingsContext";

const fade = (delay) => ({
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] } },
});

export default function Hero() {
  const { homepage } = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-background" data-testid="hero-section">
      <RouteLines className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.35]" />
      <div className="container-page relative grid lg:grid-cols-12 gap-10 lg:gap-8 items-center pt-14 pb-16 lg:pt-20 lg:pb-24">
        {/* Left: copy */}
        <div className="lg:col-span-6">
          <motion.p
            variants={fade(0)}
            initial="hidden"
            animate="show"
            className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-teal mb-6"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-8 bg-copper" />
            {homepage?.heroEyebrow || "Worldwide Trading, Sourcing and Logistics Coordination"}
          </motion.p>

          <motion.h1
            variants={fade(0.15)}
            initial="hidden"
            animate="show"
            className="font-heading font-extrabold tracking-tight text-navy text-[38px] leading-[44px] sm:text-[46px] sm:leading-[52px] lg:text-[64px] lg:leading-[70px]"
            data-testid="hero-title"
          >
            {homepage?.heroTitle || "Source globally. Coordinate confidently."}
          </motion.h1>

          <motion.p
            variants={fade(0.55)}
            initial="hidden"
            animate="show"
            className="mt-6 text-[17px] leading-[28px] lg:text-[19px] lg:leading-[30px] text-muted-foreground max-w-xl"
            data-testid="hero-subtitle"
          >
            {homepage?.heroSubtitle || "Premier Global Sourcing supports businesses with product sourcing, supplier communication, international trading and logistics coordination across selected markets in Asia, the Middle East, North America and Europe."}
          </motion.p>

          <motion.div
            variants={fade(0.7)}
            initial="hidden"
            animate="show"
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <CTAButton to="/request-a-quote" variant="primary" icon={ArrowRight} data-testid="hero-primary-cta">
              {homepage?.heroPrimaryCta || "Request a sourcing quote"}
            </CTAButton>
            <CTAButton to="/how-it-works" variant="outline" icon={PlayCircle} data-testid="hero-secondary-cta">
              {homepage?.heroSecondaryCta || "See how it works"}
            </CTAButton>
          </motion.div>

          <motion.p
            variants={fade(0.85)}
            initial="hidden"
            animate="show"
            className="mt-6 text-[13.5px] text-muted-foreground max-w-lg border-l-2 border-[#D9E0E4] pl-4"
          >
            Tell us what you need, your target quantity and destination. Our team will review the
            requirement and respond with the next steps.
          </motion.p>
        </div>

        {/* Right: clipped image with parallax + coordinates frame */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-lg border border-[#D9E0E4] bg-white">
              <motion.img
                style={{ y: imgY }}
                src="https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Container terminal with stacked shipping containers at an international port"
                width="1200"
                height="900"
                loading="eager"
                className="w-full h-[360px] sm:h-[440px] lg:h-[520px] object-cover scale-110"
              />
              {/* corner coordinate markers */}
              <div className="absolute inset-0 pointer-events-none">
                <span className="absolute top-4 left-4 text-[11px] font-mono text-white/85 tracking-wider">24.86°N · 67.00°E</span>
                <span className="absolute bottom-4 right-4 text-[11px] font-mono text-white/85 tracking-wider">KARACHI · PORT</span>
                <span className="absolute top-4 right-4 h-3 w-3 border-t-2 border-r-2 border-copper" />
                <span className="absolute bottom-4 left-4 h-3 w-3 border-b-2 border-l-2 border-copper" />
              </div>
            </div>

            {/* floating credibility tag */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -bottom-5 -left-3 sm:left-6 bg-white border border-[#D9E0E4] rounded-lg px-5 py-4 shadow-lg max-w-[240px]"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal">One point of coordination</p>
              <p className="mt-1 text-[13.5px] text-navy leading-snug">
                Requirements, suppliers, trading and logistics — in a single conversation.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
